import { Link } from 'react-router'
import { ImageNotFound } from '../../assets'
import { FabricFavorite } from '../../features/FabricExplorer/FabricFavorite'
import { Expand } from '../Animations'
import { Typography } from '../Display'
import { ImageList, ImageListItem, ImageListItemBar } from '../Images'
import { Box, Stack } from '../Layout'
import { AvailabilityChip, getAvailabilityPropsFromApi } from '../Chips/AvailabilityChip'

export function FabricCardList({
  fabrics,
  isBasic = false,
  baseUrl = '/fabrics/:fabricId',
  onClick = undefined,
}: FabricCardListProps) {
  return (
    <ImageList
      rowHeight={318}
      gap={32}
      sx={{
        justifyItems: 'center',
        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr)) !important',
        overflow: 'visible',
      }}
    >
      {fabrics.length < 1 ? (
        <Typography variant='body1'>No Results Found</Typography>
      ) : (
        fabrics.map(fabric => (
          <FabricCard key={fabric.id} fabric={fabric} isBasic={isBasic} baseUrl={baseUrl} onClick={onClick} />
        ))
      )}
    </ImageList>
  )
}

export function FabricCard({ baseUrl, onClick = undefined, ...props }: FabricCardProps) {
  const fabricId = String(props.fabric.id)
  const url = baseUrl.replace(':fabricId', fabricId).replace('fabricId=', `fabricId=${fabricId}`)

  if (onClick) {
    return (
      <Expand>
        <Box onClick={() => onClick(fabricId)}>
          <BaseFabricCard {...props} />
        </Box>
      </Expand>
    )
  }

  return (
    <Expand>
      <Link to={url} style={{ textDecoration: 'none' }}>
        <BaseFabricCard {...props} />
      </Link>
    </Expand>
  )
}

//* -------------------- COMPONENTS -------------------- *//
function BaseFabricCard(props: BaseFabricCardProps) {
  const { fabric, isBasic } = props

  return (
    <ImageListItem
      sx={{
        boxShadow: theme => theme.elevation.autocomplete,
        border: 'n100',
        width: 241,
        borderRadius: 2,
        '& img': {
          borderTopLeftRadius: 6,
          borderTopRightRadius: 6,
        },
        overflow: 'hidden',
      }}
    >
      {!isBasic && (
        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
          <AvailabilityChip {...getAvailabilityPropsFromApi(fabric.availability)} />
        </Box>
      )}
      <img
        src={`${fabric.picarioUrl}&height=500`}
        alt={fabric.description ?? fabric.trinityFabricNumber}
        loading='lazy'
        onError={e => (e.currentTarget.src = ImageNotFound)}
      />
      <ImageListItemBar
        title={<FabricDescription {...props} />}
        subtitle={
          <Typography noWrap variant='smallBody2'>
            {fabric.description}
          </Typography>
        }
        position='below'
        sx={{ pl: 2 }}
      />
    </ImageListItem>
  )
}

function FabricDescription({ fabric, isBasic }: BaseFabricCardProps) {
  const discountStyles =
    !isBasic && fabric.discount
      ? { bgcolor: 'secondary.main', py: 1, pr: 1, ml: fabric.position ? 0.25 : 3, pl: 0.25 }
      : { pr: 2 }
  const variant = !isBasic && fabric.discount ? 'smallButton' : 'subtitle1'
  const fabricPosition = fabric?.position ? `(${fabric.position})` : ''
  const displayText = isBasic
    ? `${fabricPosition}`
    : fabric.discount
      ? `${Math.ceil(Number(fabric.discount))}% off`
      : '$'.repeat(Number(fabric.priceTier))

  return (
    <Stack direction='row' alignItems='center'>
      <Typography noWrap variant='h6' sx={{ overflow: 'visible', mr: 1 }}>
        {fabric.trinityFabricNumber} {fabricPosition}
      </Typography>
      {!isBasic && <FabricFavorite fabric={fabric} />}
      <Typography variant={variant} align='right' sx={{ width: 1, ...discountStyles }}>
        {displayText}
      </Typography>
    </Stack>
  )
}

//* -------------------- TYPES -------------------- *//
type FabricsType = (TrinityAPI.FabricExtendedType | TrinityAPI.FabricFullType) & { position?: number | null }
type UrlType = ':fabricId' | 'fabricId='

interface FabricCardListProps {
  fabrics: FabricsType[]
  isBasic?: boolean
  baseUrl?: `/${string}${UrlType}${string}`
  onClick?: (fabricId: string) => void
}

interface FabricCardProps
  extends Required<Pick<FabricCardListProps, 'baseUrl' | 'isBasic'>>,
    Pick<FabricCardListProps, 'onClick'> {
  fabric: FabricsType
}

interface BaseFabricCardProps extends Pick<FabricCardProps, 'fabric' | 'isBasic'> {}
